import { c as _c } from "react-compiler-runtime";
import { createPortal } from 'react-dom';
import styled from 'foundations-theming/utils';
import { CopyIcon } from 'foundations-assets/icons/components/CopyIcon';
import I18n from 'I18n';
import { useCellSelectionSelector, useWidthsSelector } from '../../redux/reduxHooks';
import { getWidths } from '../../resizing/redux/widthsSlice';
import { useCellSelectionConfig, useCopySelectionRef, useTrackCellSelection } from '../context/CellSelectionContext';
import { isCellSelectionEnabled } from '../config';
import { getHasSelection, getIsDragging, getActiveRangeBottomRightCoord, getSelectedCellCoords } from '../redux/cellSelectionSlice';
import { getSelectedCells } from '../utils/getSelectedCells';
import { useFillHandleAnchorElement } from '../hooks/useFillHandleAnchorElement';
import { CellActionMenu } from './CellActionMenu';
import { jsx as _jsx } from "react/jsx-runtime";
const Overlay = styled.div.withConfig({
  displayName: "CellActionMenuOverlay__Overlay"
})(["position:absolute;top:50%;right:", ";transform:translateY(-50%);z-index:2;"], ({
  theme
}) => theme.space['100']);

/**
 * Renders the cell-selection action menu anchored center-right inside the active
 * selection's fill-handle cell. Always offers a built-in Copy action, followed
 * by any consumer-provided actions from the cell-selection config.
 *
 * The menu is portaled into the fill-handle `<td>` and positioned with CSS, so
 * it scrolls with the cell natively — no coordinate math required. It is hidden
 * while a drag is in progress and while the fill-handle cell is not in the DOM
 * (unmounted by virtualization).
 */
export const CellActionMenuOverlay = t0 => {
  const $ = _c(22);
  const {
    scrollContainerElementRef,
    columns,
    rows,
    getRowId
  } = t0;
  const config = useCellSelectionConfig();
  const copySelectionRef = useCopySelectionRef();
  const track = useTrackCellSelection();
  const hasSelection = useCellSelectionSelector(getHasSelection);
  const isDragging = useCellSelectionSelector(getIsDragging);
  const bottomRightCoord = useCellSelectionSelector(getActiveRangeBottomRightCoord);
  const coords = useCellSelectionSelector(getSelectedCellCoords);
  const widths = useWidthsSelector(getWidths);
  let t1;
  if ($[0] !== bottomRightCoord || $[1] !== scrollContainerElementRef || $[2] !== widths) {
    t1 = {
      coord: bottomRightCoord,
      scrollContainerElementRef,
      widths
    };
    $[0] = bottomRightCoord;
    $[1] = scrollContainerElementRef;
    $[2] = widths;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    anchorElement
  } = useFillHandleAnchorElement(t1);
  if (!isCellSelectionEnabled(config)) {
    return null;
  }
  if (!hasSelection || isDragging || !anchorElement) {
    return null;
  }
  let t2;
  let t3;
  if ($[4] !== columns || $[5] !== config || $[6] !== coords || $[7] !== copySelectionRef || $[8] !== getRowId || $[9] !== rows || $[10] !== track) {
    t3 = Symbol.for("react.early_return_sentinel");
    bb0: {
      var _config$actions;
      const cells = getSelectedCells(coords, columns, rows, getRowId);
      if (cells.length === 0) {
        t3 = null;
        break bb0;
      }
      let t4;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = I18n.text("datatable.cellSelection.copy");
        $[13] = t4;
      } else {
        t4 = $[13];
      }
      let t5;
      if ($[14] !== copySelectionRef) {
        t5 = {
          key: "copy",
          label: t4,
          Icon: CopyIcon,
          onClick: () => {
            var _copySelectionRef$cur;
            return (_copySelectionRef$cur = copySelectionRef.current) === null || _copySelectionRef$cur === void 0 ? void 0 : _copySelectionRef$cur.call(copySelectionRef, "click");
          }
        };
        $[14] = copySelectionRef;
        $[15] = t5;
      } else {
        t5 = $[15];
      }
      const copyItem = t5;
      const consumerItems = ((_config$actions = config.actions) !== null && _config$actions !== void 0 ? _config$actions : []).filter(action => action.isAvailable ? action.isAvailable(cells) : true).map(action_0 => ({
        key: action_0.key,
        label: action_0.label,
        Icon: action_0.icon,
        onClick: () => {
          track === null || track === void 0 || track({
            type: "cellSelectionActionClicked",
            actionKey: action_0.key,
            cellCount: cells.length,
            columnNames: Array.from(new Set(cells.map(_temp)))
          });
          action_0.onAction(cells);
        }
      }));
      t2 = [copyItem, ...consumerItems];
    }
    $[4] = columns;
    $[5] = config;
    $[6] = coords;
    $[7] = copySelectionRef;
    $[8] = getRowId;
    $[9] = rows;
    $[10] = track;
    $[11] = t2;
    $[12] = t3;
  } else {
    t2 = $[11];
    t3 = $[12];
  }
  if (t3 !== Symbol.for("react.early_return_sentinel")) {
    return t3;
  }
  const items = t2;
  let t4;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = I18n.text("datatable.cellSelection.actionsLabel");
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== items) {
    t5 = /*#__PURE__*/_jsx(Overlay, {
      children: /*#__PURE__*/_jsx(CellActionMenu, {
        ariaLabel: t4,
        items: items
      })
    });
    $[17] = items;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== anchorElement || $[20] !== t5) {
    t6 = /*#__PURE__*/createPortal(t5, anchorElement);
    $[19] = anchorElement;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
};
function _temp(c) {
  return c.columnName;
}